/**
 * Related Product Options
 * Handles interaction for related product switching functionality
 */

class RelatedProductOptions {
  constructor() {
    this.selectors = {
      container: '[data-related-products]',
      select: '[data-related-product-select]',
      swatch: '.f-related-product-options__swatch',
      button: '.f-related-product-options__button'
    };
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.trackAnalytics();
  }

  bindEvents() {
    // Handle dropdown changes
    document.addEventListener('change', (e) => {
      if (e.target.matches(this.selectors.select)) {
        this.handleProductChange(e.target);
      }
    });

    // Handle swatch and button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest(this.selectors.swatch) || e.target.closest(this.selectors.button)) {
        const link = e.target.closest('a');
        if (link && !link.classList.contains('f-related-product-options__swatch--unavailable') && !link.classList.contains('f-related-product-options__button--unavailable')) {
          this.handleProductSwitch(link);
        }
      }
    });

    // Handle keyboard navigation for swatches
    document.addEventListener('keydown', (e) => {
      if (e.target.matches(this.selectors.swatch) || e.target.matches(this.selectors.button)) {
        this.handleKeyboardNavigation(e);
      }
    });
  }

  handleProductChange(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const productUrl = selectedOption.value;
    const productId = selectedOption.dataset.productId;

    if (productUrl && productId) {
      this.navigateToProduct(productUrl, productId);
    }
  }

  handleProductSwitch(linkElement) {
    const productUrl = linkElement.href;
    const productId = linkElement.dataset.productId;

    if (productUrl && productId) {
      // Add loading state
      const container = linkElement.closest(this.selectors.container);
      if (container) {
        container.setAttribute('data-loading', 'true');
      }

      this.navigateToProduct(productUrl, productId);
    }
  }

  handleKeyboardNavigation(e) {
    const currentElement = e.target;
    const container = currentElement.closest(this.selectors.container);
    
    if (!container) return;

    const allOptions = container.querySelectorAll(`${this.selectors.swatch}, ${this.selectors.button}`);
    const currentIndex = Array.from(allOptions).indexOf(currentElement);

    let nextIndex = currentIndex;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        nextIndex = (currentIndex + 1) % allOptions.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        nextIndex = (currentIndex - 1 + allOptions.length) % allOptions.length;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = allOptions.length - 1;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        currentElement.click();
        return;
      default:
        return;
    }

    allOptions[nextIndex].focus();
  }

  navigateToProduct(productUrl, productId) {
    // Track the product switch for analytics
    this.trackProductSwitch(productId);

    // Navigate to the new product page
    window.location.href = productUrl;
  }

  trackProductSwitch(productId) {
    // Track analytics event for product switching
    if (typeof gtag !== 'undefined') {
      gtag('event', 'product_switch', {
        'product_id': productId,
        'event_category': 'ecommerce',
        'event_label': 'Related Product Options'
      });
    }

    // Track for Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'ViewContent', {
        content_type: 'product',
        content_ids: [productId]
      });
    }

    // Track for Shopify Analytics
    if (typeof ShopifyAnalytics !== 'undefined') {
      ShopifyAnalytics.track('Product Switched', {
        productId: productId,
        source: 'related_product_options'
      });
    }

    // Custom event for theme-specific tracking
    document.dispatchEvent(new CustomEvent('related-product-switch', {
      detail: {
        productId: productId,
        timestamp: Date.now()
      }
    }));
  }

  trackAnalytics() {
    // Track when related product options are viewed
    const containers = document.querySelectorAll(this.selectors.container);
    
    containers.forEach((container) => {
      const options = container.querySelectorAll(`${this.selectors.swatch}, ${this.selectors.button}`);
      
      if (options.length > 1) {
        // Track that related options were displayed
        if (typeof gtag !== 'undefined') {
          gtag('event', 'related_options_viewed', {
            'event_category': 'ecommerce',
            'event_label': 'Related Product Options',
            'value': options.length
          });
        }
      }
    });
  }

  // Utility method to get current product ID from page
  getCurrentProductId() {
    const productData = document.querySelector('[data-product-id]');
    return productData ? productData.dataset.productId : null;
  }

  // Method to refresh options if needed (for dynamic content)
  refresh() {
    this.init();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RelatedProductOptions();
  });
} else {
  new RelatedProductOptions();
}

// Export for module usage
window.RelatedProductOptions = RelatedProductOptions; 